/**
 * InAppBrowser Utility
 *
 * Provides in-app browser functionality for opening external links.
 * Uses Safari View Controller on iOS and Chrome Custom Tabs on Android
 * to satisfy Apple App Store Guideline 4.0 requirements.
 *
 * This ensures external links open WITHIN the app rather than
 * launching the external Safari/Chrome browser.
 */

import { Platform, Linking } from 'react-native';

/**
 * Vertiv brand colors for consistent browser styling
 */
const VERTIV_ORANGE = '#FF6B35';
const VERTIV_WHITE = '#FFFFFF';
const VERTIV_BLACK = '#000000';

/**
 * InAppBrowser configuration options
 */
export interface InAppBrowserOptions {
  /** Title to show in toolbar (Android only) */
  showTitle?: boolean;
  /** Toolbar background color */
  toolbarColor?: string;
  /** Control tint color (iOS) */
  controlTintColor?: string;
  /** Enable URL bar hiding on scroll (Android) */
  enableUrlBarHiding?: boolean;
  /** Show in recent apps (Android) */
  showInRecents?: boolean;
}

/**
 * Default options for InAppBrowser with Vertiv branding
 */
const defaultOptions: InAppBrowserOptions = {
  showTitle: true,
  toolbarColor: VERTIV_ORANGE,
  controlTintColor: VERTIV_WHITE,
  enableUrlBarHiding: true,
  showInRecents: true,
};

/**
 * Open an external URL in the in-app browser (Safari View Controller / Chrome Custom Tabs)
 *
 * This function satisfies Apple App Store Guideline 4.0 by keeping the user
 * within the app experience when opening external links.
 *
 * @param url - The URL to open
 * @param options - Optional configuration for the browser
 * @returns Promise that resolves when browser is closed
 *
 * @example
 * ```typescript
 * // Basic usage
 * await openExternalLink('https://partners.vertiv.com/');
 *
 * // With custom options
 * await openExternalLink('https://example.com', {
 *   toolbarColor: '#000000',
 *   showTitle: false,
 * });
 * ```
 */
export async function openExternalLink(
  url: string,
  options: InAppBrowserOptions = {}
): Promise<void> {
  const mergedOptions = { ...defaultOptions, ...options };

  // Web platform - use standard window.open or Linking
  if (Platform.OS === 'web') {
    if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    return;
  }

  // Native platforms - dynamically import InAppBrowser
  // This prevents webpack from trying to bundle the native module for web
  try {
    const InAppBrowser = require('react-native-inappbrowser-reborn').default;

    const isAvailable = await InAppBrowser.isAvailable();

    if (!isAvailable) {
      console.warn('InAppBrowser not available on this device, falling back to Linking.openURL');
      await Linking.openURL(url);
      return;
    }

    await InAppBrowser.open(url, {
      // iOS Safari View Controller options
      dismissButtonStyle: 'close',
      preferredBarTintColor: mergedOptions.toolbarColor,
      preferredControlTintColor: mergedOptions.controlTintColor,
      readerMode: false,
      animated: true,
      modalPresentationStyle: 'fullScreen',
      modalTransitionStyle: 'coverVertical',
      modalEnabled: true,
      enableBarCollapsing: false,
      // Note: ephemeralWebSession is for auth flows, not general browsing

      // Android Chrome Custom Tabs options
      showTitle: mergedOptions.showTitle,
      toolbarColor: mergedOptions.toolbarColor,
      secondaryToolbarColor: VERTIV_BLACK,
      navigationBarColor: VERTIV_BLACK,
      navigationBarDividerColor: VERTIV_WHITE,
      enableUrlBarHiding: mergedOptions.enableUrlBarHiding,
      enableDefaultShare: true,
      forceCloseOnRedirection: false,
      showInRecents: mergedOptions.showInRecents,

      // Animation options (Android)
      animations: {
        startEnter: 'slide_in_right',
        startExit: 'slide_out_left',
        endEnter: 'slide_in_left',
        endExit: 'slide_out_right',
      },
    });

  } catch (error) {
    console.error('❌ Failed to open InAppBrowser:', error);
    // Fallback to external browser
    try {
      await Linking.openURL(url);
    } catch (linkingError) {
      console.error('❌ Failed to open URL with Linking:', linkingError);
      throw new Error(`Unable to open URL: ${url}`);
    }
  }
}

/**
 * Close the currently open in-app browser (if any)
 *
 * @example
 * ```typescript
 * await closeInAppBrowser();
 * ```
 */
export async function closeInAppBrowser(): Promise<void> {
  if (Platform.OS === 'web') {
    return;
  }

  try {
    const InAppBrowser = require('react-native-inappbrowser-reborn').default;
    InAppBrowser.close();
  } catch (error) {
    console.warn('Failed to close InAppBrowser:', error);
  }
}

/**
 * Check if InAppBrowser is available on the current device
 *
 * @returns Promise<boolean> - true if available
 *
 * @example
 * ```typescript
 * const available = await isInAppBrowserAvailable();
 * if (available) {
 *   await openExternalLink(url);
 * } else {
 *   // Handle gracefully
 * }
 * ```
 */
export async function isInAppBrowserAvailable(): Promise<boolean> {
  if (Platform.OS === 'web') {
    return false; // Web uses window.open instead
  }

  try {
    const InAppBrowser = require('react-native-inappbrowser-reborn').default;
    return await InAppBrowser.isAvailable();
  } catch {
    return false;
  }
}
